import { Component, computed, inject } from '@angular/core';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MyAuthService } from '../../../auth/auth.service';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { Router } from '@angular/router';
import { ProjectService } from '../../../shared/services/project/project.service';
import { SessionService } from '../../../shared/services/session/session.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permissions } from '@anessa/shared-models';
import { MultiTabImportDialog } from '../../multi-tab-import/multi-tab-import-dialog';
import { MultiTabImportService } from '../../multi-tab-import/multi-tab-import.service';
import { RolePreviewDialog } from '../../role-preview-dialog/role-preview-dialog';
import { MatDialog } from '@angular/material/dialog';
import { ThemeService } from '../../../shared/services/theme.service';
import { isRouteVisibleForMultiPlant } from '../../../shared/nav/multi-plant-visibility';

@Component({
  selector: 'app-dropdown-menu',
  standalone: true,
  imports: [
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
    UpperCasePipe,
    I18nPipe,
    I18nScopeReadyDirective,
    HasPermissionDirective,
  ],
  templateUrl: './dropdown-menu-component.html',
  styleUrls: ['./dropdown-menu-component.scss'],
  providers: [provideI18nScope('ui/topbar/dropdown-menu'), I18nScopedService],
})
export class DropdownMenuComponent {
  protected readonly Permissions = Permissions;
  public readonly authService = inject(MyAuthService);
  readonly projectService = inject(ProjectService);
  protected readonly isMultiPlantProject = computed(
    () => this.projectService.currentProject()?.isMultiPlant ?? false,
  );
  private readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly multiTabImportService = inject(MultiTabImportService);
  readonly themeService = inject(ThemeService);

  /** Whether `route` should be visible in this menu given the current project's plant count. */
  protected isRouteVisible(route: string): boolean {
    return isRouteVisibleForMultiPlant(route, this.isMultiPlantProject());
  }

  onAdmin() {
    this.router.navigate(['/user-access-management']);
  }

  onAudit() {
    this.router.navigate(['/audit']);
  }

  /** Opens the "preview as role" picker (also bound to Ctrl+Alt+U in Layout). */
  onPreviewRole() {
    this.dialog.open(RolePreviewDialog, {
      width: '520px',
      panelClass: 'role-preview-panel',
    });
  }

  onLogout() {
    this.sessionService.logout();
  }

  onCloseProject() {
    this.projectService.closeProject();
  }

  onKpiManagerNavigate() {
    this.router.navigate(['/kpi-manager']).then(() => null);
  }

  onSensorMetaEditorNavigate() {
    this.router.navigate(['/sensor-meta-editor']).then(() => null);
  }

  onProjectSettingsNavigate() {
    this.router.navigate(['/project-settings']).then(() => null);
  }

  onMetricsStatusNavigate() {
    this.router.navigate(['/metrics-status']).then(() => null);
  }

  onImportStatusNavigate() {
    this.dialog
      .open(MultiTabImportDialog, {
        width: '760px',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) this.multiTabImportService.notifyImportCompleted();
      });
  }

  onToggleTheme() {
    this.sessionService.updateUserTheme();
  }
}
